import clsx from 'clsx';

import { Typography } from '../../typography';

import type { TypographyProps } from '../../typography';
import type { ComponentProps, ReactNode } from 'react';

import s from './ModalHeader.module.css';

type ModalHeaderProps = Omit<ComponentProps<'div'>, 'prefix' | 'title'> & {
  /** The main heading content */
  title: ReactNode;
  /** Description text displayed below the title */
  description?: ReactNode;
  /** Subheading text displayed above the title */
  subhead?: ReactNode;
  /** Typography color for subhead and prefix/suffix */
  subheadColor?: TypographyProps['color'];
  /** Icon or element displayed before subhead */
  subheadPrefix?: ReactNode;
  /** Icon or element displayed after subhead */
  subheadSuffix?: ReactNode;
};

export const ModalHeader = ({
  className,
  description,
  subhead,
  subheadColor,
  subheadPrefix,
  subheadSuffix,
  title,
  ...props
}: ModalHeaderProps) => {
  const hasSubhead = Boolean(subhead) || Boolean(subheadPrefix) || Boolean(subheadSuffix);

  return (
    <div className={clsx(s.header, className)} {...props}>
      {hasSubhead && (
        <div className={s.subhead}>
          {Boolean(subheadPrefix) && (
            <Typography className={s.prefix} color={subheadColor} component="span">
              {subheadPrefix}
            </Typography>
          )}

          {subhead !== undefined && (
            <Typography color={subheadColor} variant="paragraph">
              {subhead}
            </Typography>
          )}

          {Boolean(subheadSuffix) && (
            <Typography className={s.suffix} color={subheadColor} component="span">
              {subheadSuffix}
            </Typography>
          )}
        </div>
      )}

      <Typography className={s.heading} variant="section-title">
        {title}
      </Typography>

      {Boolean(description) && (
        <Typography className={s.description} color="grey-light" variant="paragraph">
          {description}
        </Typography>
      )}
    </div>
  );
};
